import { type i18n } from 'i18next';
import { AdService } from '@/api/types/services.types';
import { AdKind } from '@/api/types/common.types';
import { TerminalPaymentTargetType } from '@/api/types/payments.types';
import { setSuffix } from '@/utils/helpers';
import { BUMPS_PER_DAY } from '@/components/Ad/Promotions/Promotions.constants';

const HASH_PARTS_COUNT = 6;
const HASH_PARTS_SEPARATOR = ',';
const HASH_VALUE_SEPARATOR = '=';
const TERMINAL_OPEN = 'true';
const HASH_PREFIX = {
  isTerminalOpen: 'ot',
  actualPrice: 'ap',
  period: 'p',
  previousPrice: 'pp',
  serviceType: 'st',
  paymentTargetType: 'ptt',
};

type InitialState = {
  service: AdService;
  serviceType: AdKind | null;
  paymentTargetType: TerminalPaymentTargetType | null;
};

export function parseHashStringForTerminal(hash?: string) {
  const { actualPrice, period, previousPrice, serviceType, paymentTargetType } =
    HASH_PREFIX;
  const parts = hash?.split(HASH_PARTS_SEPARATOR);
  if (!parts || parts.length !== HASH_PARTS_COUNT) {
    return null;
  }

  const initialState: InitialState = {
    service: { price: 0, period: 0, previousPrice: null },
    serviceType: null,
    paymentTargetType: null,
  };

  return parts.reduce((result, part) => {
    const [key, value] = part.split(HASH_VALUE_SEPARATOR);
    const newService = { ...result.service };

    switch (key) {
      case actualPrice:
        newService.price = Number(value);
        return { ...result, service: newService };
      case period:
        newService.period = Number(value);
        return { ...result, service: newService };
      case previousPrice:
        newService.previousPrice = value === 'null' ? null : Number(value);
        return { ...result, service: newService };
      case serviceType:
        return { ...result, serviceType: value as AdKind };
      case paymentTargetType:
        return {
          ...result,
          paymentTargetType: value as TerminalPaymentTargetType,
        };
      default:
        return result;
    }
  }, initialState);
}

export function buildHashStringForTerminal(
  service: AdService | null,
  serviceType: AdKind | null,
  paymentTargetType: TerminalPaymentTargetType
) {
  if (!service || !serviceType) return '';
  return `${HASH_PREFIX.isTerminalOpen}${HASH_VALUE_SEPARATOR}${TERMINAL_OPEN}${HASH_PARTS_SEPARATOR}${HASH_PREFIX.actualPrice}${HASH_VALUE_SEPARATOR}${service.price}${HASH_PARTS_SEPARATOR}${HASH_PREFIX.period}${HASH_VALUE_SEPARATOR}${service.period}${HASH_PARTS_SEPARATOR}${HASH_PREFIX.previousPrice}${HASH_VALUE_SEPARATOR}${service.previousPrice}${HASH_PARTS_SEPARATOR}${HASH_PREFIX.serviceType}${HASH_VALUE_SEPARATOR}${serviceType}${HASH_PARTS_SEPARATOR}${HASH_PREFIX.paymentTargetType}${HASH_VALUE_SEPARATOR}${paymentTargetType}`;
}

export function checkHashOnTerminalPayment(hash?: string) {
  return !!hash?.includes(
    `${HASH_PREFIX.isTerminalOpen}${HASH_VALUE_SEPARATOR}${TERMINAL_OPEN}`
  );
}

export function getServiceLabel({
  i18n,
  finalService,
  finalServiceType,
}: {
  i18n: i18n;
  finalService: AdService | null;
  finalServiceType: AdKind | null;
}) {
  if (!finalService || !finalServiceType) return '';

  switch (finalServiceType) {
    case AdKind.Bumped: {
      const value = finalService.period * BUMPS_PER_DAY;
      return `${i18n.t('pages.ad.up')} ${i18n.t(
        setSuffix(i18n, value.toString(), 'common.n_times'),
        { times: value }
      )}`;
    }
    case AdKind.Vipped:
      return `${i18n.t('pages.ad.vip')} ${i18n.t(
        setSuffix(i18n, finalService.period.toString(), 'common.n_days'),
        { days: finalService.period }
      )}`;
    case AdKind.Premium:
      return `${i18n.t('pages.ad.premium')} ${i18n.t(
        setSuffix(i18n, finalService.period.toString(), 'common.n_days'),
        { days: finalService.period }
      )}`;
    default:
      return '';
  }
}

export function getBundleCountLabel(i18n: i18n, count: number) {
  return i18n.t(setSuffix(i18n, count.toString(), 'common.n_ads'), {
    n: count.toString(),
  });
}
